import { IIndicator, IInsight } from '@jumpstart/core';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import Alert from '@mui/material/Alert';
import { AlertColor } from '@mui/material/Alert/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import { SnackbarCloseReason } from '@mui/material/Snackbar/Snackbar';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../core/firebase';
import InsightNotificationsService from './insight-notifications-service';
import { InsightService } from './insight-service';
import { uiExternalUrls } from './states';
import { Transition } from './widgets/EventsList';
import IndicatorEditor from './widgets/IndicatorEditor';
import { IValidationData, validateDefault, validateIndicator } from './widgets/utils';
import ValidationPrompter from './widgets/ValidationPrompter';


export default function IndicatorPage() {
  const [user] = useAuthState(auth);
  const params = useParams<{ event: string; indicator: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: AlertColor } | null>(null);
  const [event, setEvent] = useState<IInsight | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [indicator, setIndicator] = useState<IIndicator | null>(null);
  const [validation, setValidation] = useState<IValidationData>(validateDefault());

  const reload = () => {
    setLoading(true);
    setEvent(null);
    InsightService.get(params.event as string)
      .then((e: IInsight | null) => {
        setMessage({ text: `Successfully reloaded event "${event?.name}"`, severity: 'success' });
        setLoading(false);
        setEvent(e);
        if (e) {
          const index: number = e.content.indicators.findIndex(i => i.id === params.indicator);
          if (index === -1) {
            setMessage({ text: `Failed to get indicator "${params.indicator}".`, severity: 'error' });
          } else {
            setIndicator(e.content.indicators[index]);
          }
        } else {
          setIndicator(null);
        }
      })
      .catch((err: Error) => {
        console.error(err);
        setMessage({ text: `Failed to get event "${params.event}". Error ${err.message}`, severity: 'error' });
        setLoading(false);
        setEvent(null);
        setIndicator(null);
      });
  };

  const handleOpenDialogTree = () => {
    console.log('OPEN...');
    if (event && indicator) {
      navigate(`/admin/events/dialog/${event.id}/${indicator.id}`);
    }
  };

  const handleCloseSnack = (event: React.SyntheticEvent<any> | Event, reason: SnackbarCloseReason) => {
    if (reason && reason === 'clickaway') {
      return;
    }
    setMessage(null);
  };

  const handleUpdateIndicators = (indicators: IIndicator[]): void => {
    if (!event) {
      console.error('No insight provided');
      return;
    }
    const newInsight = { ...event };
    newInsight.content.indicators = indicators.map((i: IIndicator, index) => {
      i.id = `${index}`;
      return i;
    });
    setEvent(newInsight);
    console.log('Uploading content...');
    InsightService.updateContent({ id: newInsight.id, content: newInsight.content })
      .then(() => {
        // reload();
        navigate(`/admin/events/event/${newInsight.id}`);
        return InsightNotificationsService.insight(newInsight, user as any, 'updated');
      })
      .catch((err: Error) => {
        setMessage({ text: err.message, severity: 'error' });
        console.error(err);
      });
  };

  const handleDeleteIndicator = () => {
    if (!event || !indicator) {
      return;
    }
    const newEvent = { ...event };
    const index: number = newEvent.content.indicators.findIndex(i => i.id === indicator.id);
    if (index > -1) {
      newEvent.content.indicators.splice(index, 1);
    } else {
      console.error(`Could not find indicator ${indicator.id}`);
    }
    handleUpdateIndicators(newEvent.content.indicators);
  };

  const handleChangeIndicator = (newIndicator: IIndicator & { index?: number }) => {
    if (!event) {
      return;
    }
    setIndicator(newIndicator);
    const newEvent = { ...event };
    let to: number | undefined = newIndicator.index;
    const from: number = newEvent.content.indicators.findIndex(i => i.id === newIndicator.id);
    if (from === -1) {
      // No from, must not be part of the array, add it.
      newEvent.content.indicators.push(newIndicator);
    } else {
      // Swap previous
      const oldIndicator = newEvent.content.indicators[from];
      if (oldIndicator.name === newIndicator.name) {
        newEvent.content.indicators.splice(from, 1, newIndicator);
      } else {
        // @TODO because id is tied to index, we can't remove it
        console.error('FIX ME!!');
        newEvent.content.indicators.splice(from, 0, newIndicator);
      }
    }

    if (typeof to === 'number') {
      // reorder
      newEvent.content.indicators.splice(to, 0, newEvent.content.indicators.splice(from, 1)[0]);
    }
    handleUpdateIndicators(newEvent.content.indicators);
  };

  useEffect(() => {
    if (params.event) {
      reload();
    } else {
      setEvent(null);
    }
  }, [params.event, params.indicator]);

  useEffect(() => {
    if (indicator) {
      setValidation(validateIndicator(indicator));
    } else {
      setValidation(validateDefault());
    }
  }, [indicator]);

  if (loading) {
    return null;
  }

  return (
    <Container maxWidth="xl" disableGutters>
      <Grid container direction="column">
        <Snackbar open={!!message} autoHideDuration={6000} onClose={handleCloseSnack}>
          <Alert onClose={() => setMessage(null)} severity={message ? message.severity : 'warning'}>
            {message ? message.text : ''}
          </Alert>
        </Snackbar>
        <Grid item>
          <Breadcrumbs>
            <Link color="inherit" to={`/admin/events/select`} component={RouterLink}>
              Events
            </Link>
            {event &&
            <Link color="inherit" to={`/admin/events/event/${event.id}`} component={RouterLink}>{event.name}</Link>}

            {event && indicator && (
              <Grid container direction="row" wrap="nowrap">
                <Typography color="textPrimary">{indicator.name}</Typography>
                <Link
                  href={uiExternalUrls.indicator(event, indicator)}
                  target={`scout9-beta-mobile-${event.id}-${indicator.id}`}
                  style={{ height: 20, marginLeft: 8 }}
                >
                  <LinkIcon fontSize="small"/>
                </Link>
              </Grid>
            )}
          </Breadcrumbs>
        </Grid>


        <Grid item container style={{ marginBottom: 44 }}>
          <Grid container direction="row" wrap="nowrap" style={{ padding: 24 }}>
            <Grid item xs={7}>
              <Paper style={{ height: '100%', padding: 24 }} elevation={0}>
                <Grid container direction="row">

                  <Grid item style={{ marginRight: 24 }}>
                    <ButtonBase onClick={handleOpenDialogTree}
                                sx={{
                                  transition: '300ms all',
                                  borderRadius: 4,
                                  '&:hover': {
                                    backgroundColor: '#FAFAFA',
                                    transform: 'scale(1.05)'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                    >
                      <Card
                        sx={{
                          width: 144,
                          height: 144,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <div style={{ flexGrow: 1 }}/>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute'
                          }}>
                          <AccountTreeIcon fontSize="large" style={{ flexGrow: 1, fontSize: 44, opacity: 0.7 }}/>
                        </div>
                        <div>
                          <Typography color="textSecondary" gutterBottom style={{ width: 105 }}>
                            Open Guidance Tree
                          </Typography>
                        </div>
                      </Card>
                    </ButtonBase>
                  </Grid>

                  <Grid item>
                    <ButtonBase
                      onClick={() => setConfirmingDelete(true)}
                      sx={{
                        transition: '300ms all',
                        borderRadius: 4,
                        '&:hover': {
                          backgroundColor: '#FAFAFA',
                          transform: 'scale(1.05)'
                        },
                        '&:active': {
                          transform: 'scale(0.95)'
                        }
                      }}

                    >
                      <Card
                        sx={{
                          width: 144,
                          height: 144,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <div style={{ flexGrow: 1 }}/>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute'
                          }}>
                          <DeleteIcon fontSize="large" style={{ flexGrow: 1, fontSize: 44, opacity: 0.7 }}/>
                        </div>
                        <div>
                          <Typography color="textSecondary" gutterBottom style={{ width: 105 }}>
                            Delete Indicator
                          </Typography>
                        </div>
                      </Card>
                    </ButtonBase>
                    <Dialog
                      TransitionComponent={Transition}
                      open={!!confirmingDelete}
                      onClose={() => setConfirmingDelete(false)}
                    >
                      <DialogTitle>Remove Indicator</DialogTitle>
                      <DialogContent>
                        <DialogContentText>Are you sure you want to delete this indicator?<br/><br/><b>This will erase
                          all
                          content data</b></DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button autoFocus onClick={() => setConfirmingDelete(false)}>
                          No
                        </Button>
                        <Button onClick={handleDeleteIndicator}>
                          Yes
                        </Button>
                      </DialogActions>
                    </Dialog>

                  </Grid>

                </Grid>

              </Paper>
            </Grid>

            {(validation.numErrors > 0 || validation.numWarnings > 0) &&
            <Grid item xs={5} style={{ flexGrow: 1 }}>
              <Paper style={{ height: '100%', padding: 24 }}>
                <Grid item container direction="column">
                  <Collapse in={validation.numErrors > 0}>
                    <Grid item style={{ marginBottom: 12 }}>
                      <ValidationPrompter messages={validation.errors} severity="error"
                                          title={validation.numErrors + ' Errors'}/>
                    </Grid>
                  </Collapse>

                  <Collapse in={validation.numWarnings > 0}>
                    <Grid item style={{ marginBottom: 12 }}>
                      <ValidationPrompter messages={validation.warnings} severity="warning"
                                          title={validation.numWarnings + ' Warnings'}/>
                    </Grid>
                  </Collapse>
                </Grid>

              </Paper>
            </Grid>}
          </Grid>

          <Grid item container>
            {/*{*/}
            {/*  event && indicator && <IndicatorForm*/}
            {/*  indicator={{...indicator, index: event.content.indicators.findIndex(i => i.id === indicator.id)}}*/}
            {/*  max={event.content.indicators.length - 1}*/}
            {/*  onChange={handleChangeIndicator}*/}
            {/*/>*/}
            {/*}*/}
            {event && indicator && <IndicatorEditor
              event={event}
              indicator={{ ...indicator, index: parseInt(indicator.id, 10) }}
              loading={loading}
              max={event.content.indicators.length - 1}
              onSubmit={handleChangeIndicator}
              validation={{ ...validation.errors, ...validation.errors }}
            />}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
